// DocuSign — Azure Function endpoints (OAuth + envelopes). HTTP routes are authLevel anonymous;
// the refresh token lives in Function App configuration, not in the browser.
class DocuSignService {
  constructor() {
    this.functionAppUrl =
      'https://wolfwalker-api-b7abdbh3apbdavga.canadacentral-01.azurewebsites.net';
  }

  getOAuthStartUrl() {
    return `${this.functionAppUrl}/api/docusign/oauth/start`;
  }

  async _fetchJson(path) {
    const url = `${this.functionAppUrl}${path}`;
    const response = await fetch(url);
    const text = await response.text();
    let body;
    try {
      body = text ? JSON.parse(text) : {};
    } catch {
      body = { _parseError: true, raw: text };
    }
    return { ok: response.ok, status: response.status, statusText: response.statusText, body };
  }

  async fetchMe() {
    return this._fetchJson('/api/docusign/me');
  }

  /**
   * @param {Record<string, string>} [query] — e.g. { all_users: 'true', from_date: '2020-01-01', folder_ids: 'awaiting_my_signature' }
   */
  async fetchEnvelopes(query = {}) {
    const params = new URLSearchParams(query);
    const qs = params.toString();
    const path = qs ? `/api/docusign/envelopes?${qs}` : '/api/docusign/envelopes';
    return this._fetchJson(path);
  }

  async fetchDebugBatch() {
    return this._fetchJson('/api/docusign/debug');
  }

  /**
   * DocuSign queue for Lab Approvals (server-side status filter).
   * @param {{ status?: 'sent'|'delivered'|'completed'|'all' }} [options] — default sent (sent+delivered)
   */
  async fetchActionRequiredEnvelopes(options = {}) {
    const status = (options.status || 'sent').trim().toLowerCase();
    const params = new URLSearchParams({
      search_folder: 'action_required',
      from_date: '2020-01-01',
      status,
    });
    return this._fetchJson(`/api/docusign/envelopes?${params.toString()}`);
  }

  /** @deprecated use fetchActionRequiredEnvelopes */
  async fetchAwaitingMySignature() {
    return this.fetchActionRequiredEnvelopes({ status: 'sent' });
  }

  /**
   * Time-limited DocuSign web URL (recipient signing preferred, else console view).
   * @param {string} [returnUrl] — DocuSign redirect after session (e.g. back to Lab Approvals).
   */
  async fetchEnvelopeWebUrl(envelopeId, returnUrl) {
    const id = (envelopeId || '').trim();
    if (!id) {
      throw new Error('envelope_id is required');
    }
    const params = new URLSearchParams({ envelope_id: id });
    if (returnUrl) {
      params.set('return_url', returnUrl);
    }
    return this._fetchJson(`/api/docusign/envelope_web_url?${params.toString()}`);
  }

  /**
   * Same as envelope_web_url but served as GET → 302 so window.open works synchronously (no about:blank).
   */
  getEnvelopeWebRedirectUrl(envelopeId, returnUrl) {
    const id = (envelopeId || '').trim();
    if (!id) {
      throw new Error('envelope_id is required');
    }
    const params = new URLSearchParams({ envelope_id: id });
    if (returnUrl) {
      params.set('return_url', returnUrl);
    }
    return `${this.functionAppUrl}/api/docusign/envelope_web_url_redirect?${params.toString()}`;
  }

  /**
   * Segment plan for multi-workorder envelopes (picker labels + document_id/page for download).
   * @returns {Promise<{ segments: Array, segmentCount: number, multi: boolean }>}
   */
  async fetchEnvelopeSegments(envelopeId, { withHints = true } = {}) {
    const id = (envelopeId || '').trim();
    if (!id) {
      throw new Error('envelope_id is required');
    }
    const params = new URLSearchParams({ envelope_id: id });
    if (!withHints) {
      params.set('hints', 'false');
    }
    const res = await this._fetchJson(
      `/api/docusign/envelope_documents?${params.toString()}`
    );
    if (!res.body?.success) {
      throw new Error(res.body?.error || `envelope_documents failed (${res.status})`);
    }
    return res.body.data || { segments: [], segmentCount: 0, multi: false };
  }

  /**
   * Envelope PDF for AI extract (binary).
   * @param {string} envelopeId
   * @param {{ documentId?: string|null, page?: number|null }} [segment]
   */
  async fetchEnvelopePdfBlob(envelopeId, segment = {}) {
    const id = (envelopeId || '').trim();
    if (!id) {
      throw new Error('envelope_id is required');
    }
    const params = new URLSearchParams({ envelope_id: id });
    const docId = segment.documentId ?? segment.document_id;
    if (docId != null && String(docId).trim() !== '') {
      params.set('document_id', String(docId).trim());
    }
    const page = segment.page;
    if (page != null && String(page).trim() !== '') {
      params.set('page', String(page));
    }
    const url = `${this.functionAppUrl}/api/docusign/envelope_document?${params.toString()}`;
    const response = await fetch(url);
    if (!response.ok) {
      const text = await response.text();
      let msg = text;
      try {
        const j = JSON.parse(text);
        if (j && j.error) msg = String(j.error);
      } catch {
        /* keep text */
      }
      throw new Error(msg || `envelope_document failed (${response.status})`);
    }
    const ct = (response.headers.get('content-type') || '').toLowerCase();
    if (ct.includes('application/json')) {
      const j = await response.json();
      throw new Error((j && j.error) || 'Unexpected JSON from envelope_document');
    }
    return response.blob();
  }

  /** Combined envelope PDF for AI extract (binary). */
  async fetchEnvelopeCombinedPdfBlob(envelopeId) {
    return this.fetchEnvelopePdfBlob(envelopeId);
  }
}

export const docusignService = new DocuSignService();
export default docusignService;
